/**
 * The application API client — task P3-06.
 *
 * Everything here is typed by `@ch/client`, which is generated from the
 * committed OpenAPI document, which is generated from the Fastify routes' Zod
 * schemas. The SPA therefore cannot describe a request the server does not
 * accept, because it has no way to spell one.
 */
import {
    ApiError,
    createClient,
    type ChartResponse,
    type DeviceSummary,
} from "@ch/client";

export { ApiError } from "@ch/client";
export type { ChartResponse, DeviceSummary, ChartPoint } from "@ch/client";

export const api = createClient();

export type Me = {
    user: { id: string; email: string; name: string | null; emailVerified: boolean };
    account: { accountId: string; name: string; role: "owner" | "admin" | "viewer" };
    plan: { code: string; maxDevices: number; retentionDays: number };
    deviceCount: number;
};

export type Member = {
    userId: string;
    email: string;
    name: string | null;
    role: "owner" | "admin" | "viewer";
    joinedAt: string;
};

/**
 * Routes the generated client does not cover yet.
 *
 * `@ch/client` is regenerated by `pnpm openapi` and grows methods for these as
 * the spec does; until then, these calls go through the same fetch conventions
 * so error handling stays uniform.
 */
async function request<T>(
    path: string,
    init: RequestInit = {},
): Promise<T> {
    const response = await fetch(path, {
        headers: { accept: "application/json", ...(init.headers ?? {}) },
        ...init,
    });

    if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
            error?: string;
            message?: string;
        } | null;
        throw new ApiError(
            response.status,
            body?.error ?? "http_error",
            body?.message ?? response.statusText,
        );
    }

    return response.status === 204 ? (null as T) : ((await response.json()) as T);
}

const json = (body: unknown): RequestInit => ({
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
});

export const getMe = (): Promise<Me> => request<Me>("/api/v1/me");

export const listDevices = (): Promise<{ devices: DeviceSummary[] }> =>
    api.listDevices();

export const getChart = (
    deviceId: string,
    from: number,
    to: number,
    signal?: AbortSignal,
): Promise<ChartResponse> =>
    api.getDeviceChart(deviceId, { from, to }, signal ? { signal } : {});

export const claimDevice = (chId: string, code: string) =>
    request<{ deviceId: string; chId: string }>(
        "/api/v1/devices/claim",
        json({ chId, code }),
    );

export const claimDeviceFromQr = (qrPayload: string) =>
    request<{ deviceId: string; chId: string }>(
        "/api/v1/devices/claim",
        json({ qrPayload }),
    );

export const releaseDevice = (deviceId: string) =>
    request<null>(`/api/v1/devices/${encodeURIComponent(deviceId)}/claim`, {
        method: "DELETE",
    });

export const listMembers = () =>
    request<{ members: Member[] }>("/api/v1/account/members");

export const updateMemberRole = (userId: string, role: Member["role"]) =>
    request<null>(`/api/v1/account/members/${encodeURIComponent(userId)}`, {
        ...json({ role }),
        method: "PATCH",
    });

export const updateDeviceSettings = (deviceId: string, timezone: string) =>
    request<{ deviceId: string; chId: string; timezone: string }>(
        `/api/v1/devices/${encodeURIComponent(deviceId)}/settings`,
        { ...json({ timezone }), method: "PATCH" },
    );
